import { Action, Dispatch } from 'redux';

import {
  ATTACHMENT_AWS_URL,
  ATTACHMENT_CLOUD_FILES_URL,
  BPIT_PREFIX,
} from 'appConstants';

import { AttachmentAWS, AttachmentAWSData } from '../types';
import { ApiRequest, AttachmentUtils } from 'services';

/* Types */
export interface AttachmentsAWSAction extends Action {
  readonly attachment?: AttachmentAWS;
  readonly collectionKey?: string;
}

export interface AttachmentAWSItems {
  [collectionKey: string]: AttachmentAWS[];
}

export interface AttachmentsAWS {
  readonly items: AttachmentAWSItems;
}

export interface AttachmentAWSPayload {
  filename: string;
  file: File;
  collectionKey: string;
}

/* Initial State */
const initialState: AttachmentsAWS = {
  items: {},
};

/* Actions */
enum Actions {
  REMOVE_ATTACHMENT_AWS = 'REMOVE_ATTACHMENT_AWS',
  RESET_ATTACHMENT_STATE_AWS = 'RESET_ATTACHMENT_STATE_AWS',
  UPSERT_ATTACHMENT_AWS = 'UPSERT_ATTACHMENT_AWS',
}

const removeAttachmentAWS = (
  attachment: AttachmentAWS,
  collectionKey: string,
): AttachmentsAWSAction => ({
  type: Actions.REMOVE_ATTACHMENT_AWS,
  attachment,
  collectionKey,
});

const resetAttachmentAWSState = (): AttachmentsAWSAction => ({
  type: Actions.RESET_ATTACHMENT_STATE_AWS,
});

const upsertAttachmentAWS = (
  attachment: AttachmentAWS,
  collectionKey: string,
): AttachmentsAWSAction => ({
  type: Actions.UPSERT_ATTACHMENT_AWS,
  attachment,
  collectionKey,
});

/* Reducer */
const attachmentsAWSReducer = (
  state = initialState,
  action: AttachmentsAWSAction,
): AttachmentsAWS => {
  switch (action.type) {
    case Actions.UPSERT_ATTACHMENT_AWS:
      return {
        ...state,
        ...(action.collectionKey && {
          items: {
            ...state.items,
            [action.collectionKey]: [
              ...(
                state.items[action.collectionKey] || ([] as AttachmentAWS[])
              ).filter(({ id }) => {
                if (!action.attachment) {
                  return false;
                }
                return id !== action.attachment.id;
              }),
              action.attachment || ({} as AttachmentAWS),
            ],
          },
        }),
      };
    case Actions.REMOVE_ATTACHMENT_AWS:
      return {
        ...state,
        ...(action.collectionKey &&
          action.attachment && {
            items: {
              ...state.items,
              [action.collectionKey]: state.items[action.collectionKey].filter(
                ({ data }) => {
                  if (!data || !action.attachment || !action.attachment.data) {
                    return false;
                  }
                  return data.versionId !== action.attachment.data.versionId;
                },
              ),
            },
          }),
      };
    case Actions.RESET_ATTACHMENT_STATE_AWS:
      return {
        ...state,
        items: {},
      };
    default:
      return state;
  }
};

/* Thunks */

const uploadAWSAttachment = (
  files: File[],
  collectionKey: string,
  uploadToCloudFiles: boolean,
) => async (dispatch: Dispatch<AttachmentsAWSAction>) => {
  const body = new FormData();
  files.forEach((file) => {
    body.append('file', file);
    let url = '';
    if (uploadToCloudFiles) {
      url = ATTACHMENT_CLOUD_FILES_URL + '/' + file.name;
    } else {
      url = ATTACHMENT_AWS_URL + '/' + file.name;
    }
    const onUpdate = (result: AttachmentAWS) => {
      dispatch(upsertAttachmentAWS(result, collectionKey));
    };
    const fileInfo = AttachmentUtils.processFile(file);
    const fileData = AttachmentUtils.composeAttachmentAWS(
      fileInfo,
      {} as AttachmentAWSData,
    );
    ApiRequest.upload(fileData, body, url, onUpdate, true, {
      Accept: 'application/json',
    });
    dispatch(upsertAttachmentAWS(fileData, collectionKey));
  });
};

const deleteAWSAttachment = (
  attachment: AttachmentAWS,
  collectionKey: string,
  deleteFromCloudFiles: boolean,
) => async (dispatch: Dispatch<AttachmentsAWSAction>) => {
  if (attachment.data && attachment.errors.size === 0) {
    const versionIdHeaders = {
      'Version-Id': attachment.data.versionId,
    };

    const onSuccess = () =>
      dispatch(removeAttachmentAWS(attachment, collectionKey));

    // OnError TODO

    let url = '';
    if (deleteFromCloudFiles) {
      url = BPIT_PREFIX + attachment.data.deleteUrl;
    } else {
      url = ATTACHMENT_AWS_URL + '/' + attachment.data.name;
    }
    return ApiRequest.delete(url, {
      headers: versionIdHeaders,
      onSuccess,
    });
  } else {
    dispatch(removeAttachmentAWS(attachment, collectionKey));
  }
};

export {
  attachmentsAWSReducer,
  removeAttachmentAWS,
  resetAttachmentAWSState,
  uploadAWSAttachment,
  deleteAWSAttachment,
  upsertAttachmentAWS,
};
